// Canonical email-template slug registry — the SINGLE source of truth.
// Consumed by: admin (labels + automation dropdown), seeds, and system send-code.
// Slugs are ASCII snake_case slovak. Do NOT rename a slug without a data migration.

export interface EmailTemplateVar {
  name: string
  description: string
  example: string
}

export interface EmailTemplateDef {
  /** Slovak label shown in admin UI. */
  label: string
  /** Slovak one-line explanation of exactly when this template is sent (admin tooltip). */
  description: string
  /** 'system' = sent automatically by code on a fixed event; 'automation' = picked in an automation rule. */
  kind: 'system' | 'automation'
  /** The real event this template is tied to (NOTIFICATION/AUTOMATION trigger key, or a code-hook name). */
  event: string
  /** Handlebars variables available in subject/body. */
  vars: EmailTemplateVar[]
}

const v = (name: string, description: string, example: string): EmailTemplateVar => ({
  name,
  description,
  example,
})

export const EMAIL_TEMPLATE_SLUGS = {
  // ── System (sent automatically by code) ──────────────────────────────────
  novy_kontakt: {
    label: 'Nový kontakt (maklérovi)',
    description: 'Systémová · odošle sa automaticky maklérovi, keď ho niekto iný pridá ako agenta kontaktu (aj pri založení nového kontaktu).',
    kind: 'system',
    event: 'lead.assigned',
    vars: [
      v('agentName', 'Meno makléra', 'Jana Kováčová'),
      v('contactName', 'Meno záujemcu', 'Ján Novák'),
      v('contactEmail', 'Email záujemcu', 'jan.novak@example.com'),
      v('contactPhone', 'Telefón záujemcu', '+421 900 123 456'),
      v('message', 'Správa záujemcu', 'Mám záujem o túto nehnuteľnosť.'),
      v('propertyTitle', 'Názov nehnuteľnosti', '3-izbový byt, Ružinov'),
      v('leadSource', 'Zdroj kontaktu', 'Nehnuteľnosti.sk'),
    ],
  },
  reset_hesla: {
    label: 'Reset hesla',
    description: 'Systémová · odošle sa automaticky používateľovi pri žiadosti o resetovanie hesla.',
    kind: 'system',
    event: 'auth.password_reset',
    vars: [
      v('firstName', 'Meno používateľa', 'Ján'),
      v('resetUrl', 'Odkaz na nastavenie hesla', 'https://app.realnexa.sk/reset-password?token=…'),
      v('appName', 'Názov aplikácie', 'Realnexa'),
    ],
  },
  pozvanka_pouzivatela: {
    label: 'Pozvánka používateľa',
    description: 'Systémová · odošle sa automaticky novému používateľovi pri vytvorení účtu (s dočasným heslom).',
    kind: 'system',
    event: 'user.invited',
    vars: [
      v('firstName', 'Meno používateľa', 'Ján'),
      v('email', 'Prihlasovací email', 'jan.novak@example.com'),
      v('password', 'Dočasné heslo', 'Tmp1234!'),
      v('tenantName', 'Názov firmy', 'Reality Premium s.r.o.'),
    ],
  },
  // ── Automation (picked in a rule; event = rule trigger) ──────────────────
  odpoved_na_dopyt: {
    label: 'Odpoveď na dopyt (zákazníkovi)',
    description: 'Automatizácia · vyberte ju v pravidle pre udalosť „Nový dopyt" (deal.created).',
    kind: 'automation',
    event: 'deal.created',
    vars: [
      v('contactName', 'Meno zákazníka', 'Ján Novák'),
      v('propertyTitle', 'Názov nehnuteľnosti', '3-izbový byt, Ružinov'),
      v('agentName', 'Meno makléra', 'Lucia Bartošová'),
      v('agentPhone', 'Telefón makléra', '+421 911 400 002'),
    ],
  },
  obhliadka_potvrdena: {
    label: 'Potvrdenie obhliadky',
    description: 'Automatizácia · vyberte ju v pravidle pre udalosť „Nová obhliadka" (viewing.created).',
    kind: 'automation',
    event: 'viewing.created',
    vars: [
      v('contactName', 'Meno zákazníka', 'Jana Kováčová'),
      v('propertyTitle', 'Názov nehnuteľnosti', '2-izbový byt, Staré Mesto'),
      v('viewingDate', 'Dátum a čas obhliadky', '15.4.2026 o 14:00'),
      v('propertyAddress', 'Adresa nehnuteľnosti', 'Laurinská 8, Bratislava'),
      v('agentName', 'Meno makléra', 'Lucia Bartošová'),
      v('agentPhone', 'Telefón makléra', '+421 911 400 002'),
    ],
  },
  obchod_uzavrety: {
    label: 'Uzavretý obchod',
    description: 'Automatizácia · vyberte ju v pravidle pre udalosť „Uzavretý obchod" (deal.won).',
    kind: 'automation',
    event: 'deal.won',
    vars: [
      v('dealName', 'Názov obchodu', 'Predaj 3i byt Ružinov'),
      v('value', 'Hodnota obchodu', '185000'),
      v('currency', 'Mena', 'EUR'),
    ],
  },
  obhliadka_zrusena: {
    label: 'Zrušenie obhliadky',
    description: 'Automatizácia · vyberte ju v pravidle pre udalosť „Zrušenie obhliadky" (viewing.cancelled).',
    kind: 'automation',
    event: 'viewing.cancelled',
    vars: [
      v('propertyTitle', 'Názov nehnuteľnosti', '2-izbový byt, Staré Mesto'),
      v('viewingDate', 'Pôvodný dátum a čas', '15.4.2026 o 14:00'),
      v('status', 'Stav', 'CANCELLED'),
    ],
  },
} as const satisfies Record<string, EmailTemplateDef>

export type EmailTemplateSlug = keyof typeof EMAIL_TEMPLATE_SLUGS

export const EMAIL_TEMPLATE_SLUG_LIST = Object.keys(EMAIL_TEMPLATE_SLUGS) as EmailTemplateSlug[]

export const AUTOMATION_TEMPLATE_SLUGS = EMAIL_TEMPLATE_SLUG_LIST.filter(
  (s) => EMAIL_TEMPLATE_SLUGS[s].kind === 'automation',
)

export function getEmailTemplateLabel(slug: string): string {
  return (EMAIL_TEMPLATE_SLUGS as Record<string, EmailTemplateDef>)[slug]?.label ?? slug
}
